
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  Ref,
  ref,
  toRefs,
  watch,
} from "vue";
import BaseFormFieldInput from "../components/BaseFormFieldInput.vue";
import InputFieldFeedbackStatus from "../enum/InputFieldFeedbackStatus.enum";
import { dateToString, removeTimeFromDate } from "../utils/date";
import SectionForCheckBox from "../interfaces/Section/SectionForCheckBox.interface";
import AvailableRoom from "../interfaces/Room/AvailableRoom.interface";
import BaseFormConfig from "../interfaces/config/BaseFormConfig.interface";
import Section from "../interfaces/Section/Section.interface";
import ReservationForm from "../interfaces/Reservation/ReservationForm.interface";
import { useStore } from "../store";
import SectionWithDisable from "../interfaces/Section/SectionWithDisable.interface";
import { AvailableRoomsToReservationForm } from "../utils/reservationUtils";
import BackButton from "./BackButton.vue";
import GETAvailableSections from "../interfaces/Section/GETAvailableSections.interface";
export default defineComponent({
  name: "BaseReservationForm",
  components: { BaseFormFieldInput, BackButton },
  props: {
    config: {
      required: true,
      type: Object as () => BaseFormConfig,
    },
    baseReservation: {
      required: false,
      type: Object as () => ReservationForm,
    },
    reservationId: {
      required: false,
      type: Number,
    },
  },
  setup(props) {
    const store = useStore();

    //Object containing all the information to be used in the form
    const registerInformation: ReservationForm = reactive(
      Object.assign(
        {},
        props.baseReservation ?? {
          roomCode: "",
          sections: [] as Array<SectionWithDisable>,
          reservationText: "",
          startDate: dateToString(removeTimeFromDate(new Date())),
          startTime: "",
          endDate: "",
          endTime: "",
          amountOfPeople: "",
        }
      )
    );

    //Rooms
    const roomStatus = ref(InputFieldFeedbackStatus.NONE);

    /**
     * Sets roomStatus to succes if the selected roomCode exists in the the rooms to be selected from, else it sets an ERROR
     */
    const checkRoomValidity = () => {
      if (
        rooms.value.some(
          (room) => room.roomCode === registerInformation.roomCode
        )
      ) {
        roomStatus.value = InputFieldFeedbackStatus.SUCCESS;
      } else roomStatus.value = InputFieldFeedbackStatus.ERROR;
    };

    //TODO get rooms and section based on the selected date and time
    const rooms: Ref<Array<AvailableRoom>> = ref([]);

    /**
     * Sorts the rooms alphabetically based on the room code when the rooms change
     */
    watch(
      () => rooms.value,
      () => {
        rooms.value.sort((a, b) => {
          if (a.roomCode.toLowerCase() < b.roomCode.toLowerCase()) {
            return -1;
          }
          if (a.roomCode.toLowerCase() > b.roomCode.toLowerCase()) {
            return 1;
          }
          return 0;
        });
      }
    );

    //Sections
    const availableSections: Ref<SectionForCheckBox[]> = ref([]);
    const sectionStatus = ref(InputFieldFeedbackStatus.NONE);
    /**
     * Checks if at least on section is selected, and sets the section status
     */
    const checkSectionValidity = () => {
      sectionStatus.value =
        registerInformation.sections.length !== 0
          ? InputFieldFeedbackStatus.SUCCESS
          : InputFieldFeedbackStatus.ERROR;
    };

    /**
     * Maps all the sections from the selected room to SectionForCheckBox,where the initial selected value is false
     * Need a watcher since computed object cannot be mutated
     */
    const mapSections = () => {
      const sections: SectionWithDisable[] =
        rooms.value.find((room) => {
          return room.roomCode === registerInformation.roomCode;
        })?.sections ?? [];
      availableSections.value =
        sections?.map((s: SectionWithDisable) => {
          return { ...s, selected: false };
        }) ?? [];
    };

    /**
     * When a change in the selected roomCode occurs, run the mapSections function, and clear all the sections
     */
    watch(
      () => registerInformation.roomCode,
      () => {
        registerInformation.sections = [];
        mapSections();
      }
    );

    /**
     * runs mapSection
     * If there are passed selected sections to the config object, then make them selected
     */
    onMounted(async () => {
      if (props.baseReservation !== undefined) {
        await getAvailableRooms();
      }

      mapSections();
      if (registerInformation.sections.length !== 0) {
        availableSections.value.forEach((section) => {
          const index = registerInformation.sections.findIndex(
            (s) => s.sectionName === section.sectionName
          );
          if (index >= 0) {
            availableSections.value[index].selected = true;
          }
        });
      }
    });

    /**
     * Selects or deselects a section
     * Adds or removes the section name to/from the registerInformation object (depending on the check value)
     * @param section The section to be selected or deselected
     */
    const handleCheckBoxChange = (section: SectionForCheckBox) => {
      section.selected = !section.selected;
      if (section.selected) {
        registerInformation.sections.push({
          sectionName: section.sectionName,
          isDisabled: false,
        });
      } else {
        registerInformation.sections.splice(
          registerInformation.sections.findIndex(
            (s) => s.sectionName === section.sectionName
          ),
          1
        );
      }
    };

    /**
     * Selects all of the sections for the current roomCode
     * Adds all sections belonging to the current roomCode to the registerInformation  object
     */
    const selectAll = () => {
      availableSections.value.forEach((sectionForCheckBox) => {
        if (sectionForCheckBox.isDisabled) return;

        sectionForCheckBox.selected = true;
        if (
          !registerInformation.sections.some(
            (section) => section.sectionName === sectionForCheckBox.sectionName
          )
        )
          registerInformation.sections.push({
            sectionName: sectionForCheckBox.sectionName,
            isDisabled: false,
          });
      });
    };

    /**
     * Removes all of the sections for the current roomCode
     * Removes all sections belonging to the current roomCode of the registerInformation object
     */
    const removeAll = () => {
      availableSections.value.forEach((sectionForCheckBox) => {
        sectionForCheckBox.selected = false;
      });
      registerInformation.sections = [];
    };

    const currentDate = new Date();
    //The minimum allowed date
    const minDate = removeTimeFromDate(currentDate);
    //Not allowed too book reservation more than half a year in advance
    const longestTimeInFutureAllowedInMonths = 6;
    //Not allow to reserve a room for more than a week
    const maxDaysDifferenceBetweenStartAndEndDate = 7;

    const maxDate = removeTimeFromDate(currentDate);
    maxDate.setMonth(minDate.getMonth() + longestTimeInFutureAllowedInMonths);

    const minDateString = ref(dateToString(minDate));
    const maxDateString = ref(dateToString(maxDate));

    /**
     * Returns the startDate from the datePicker as a date, to be ble to do mathematical operations with it
     */
    const startDateAsDate = computed(
      () => new Date(registerInformation.startDate)
    );

    /**
     * Returns the endDate from the datePicker as a date, to be ble to do mathematical operations with it
     */
    const endDateAsDate = computed(() => new Date(registerInformation.endDate));

    //Is NONE when an empty start time is passed, or Success when a startime is passed
    const startDateStatus = ref(
      registerInformation.startTime === ""
        ? InputFieldFeedbackStatus.NONE
        : InputFieldFeedbackStatus.SUCCESS
    );

    /**
     * Sets startDateStatus to SUCCESS if its a valid date
     */
    const checkStartDateValidity = () => {
      startDateStatus.value =
        startDateAsDate.value <= maxDate &&
        startDateAsDate.value >= minDate &&
        registerInformation.startDate !== ""
          ? InputFieldFeedbackStatus.SUCCESS
          : InputFieldFeedbackStatus.ERROR;
    };

    const startDateEndDateDifferenceInDays = ref(0);

    /**
     * Finds the difference between the start date and end date in days
     */
    const checkDifferenceStartDateEndDate = () => {
      //Not using Math.abs since end date always is equal to or above start date
      if (
        registerInformation.startDate.trim() === "" ||
        registerInformation.endDate.trim() === ""
      ) {
        startDateEndDateDifferenceInDays.value = 0;
        return;
      }
      const difference =
        endDateAsDate.value.getTime() - startDateAsDate.value.getTime();
      startDateEndDateDifferenceInDays.value = Math.ceil(
        difference / (24 * 3600 * 1000)
      );
    };

    /**
     * Assigns end date based on the selected start date
     * Using the difference in days between the start date and end date = x.
     * The end date is set x dates after the start date.
     * Meaning that when a users selects a date 3 months from now the user does not need to go 3 months in the future when using the end date datpicker
     */
    const assignEndDate = () => {
      if (registerInformation.startDate.trim() === "") return;
      if (startDateEndDateDifferenceInDays.value > 0) {
        const date = new Date(startDateAsDate.value);
        date.setDate(date.getDate() + startDateEndDateDifferenceInDays.value);
        registerInformation.endDate = dateToString(date);
        return;
      }
      registerInformation.endDate = registerInformation.startDate;
    };

    /**
     * Calculates error message for starDate based on the constraints available
     */
    const startDateErrorMessage = computed(
      () =>
        `Select a date between ${minDateString.value} and ${maxDateString.value}`
    );

    //End date
    const endDateStatus = ref(InputFieldFeedbackStatus.NONE);

    /**
     * Sets endDateStatus to SUCCESS if endDate is valid, or ERROR if it is not valid
     */
    const checkEndDateValidity = () => {
      endDateStatus.value =
        endDateAsDate.value <= maxDate &&
        endDateAsDate.value >= minDate &&
        endDateAsDate.value >= startDateAsDate.value &&
        registerInformation.endDate !== ""
          ? InputFieldFeedbackStatus.SUCCESS
          : InputFieldFeedbackStatus.ERROR;
    };

    /**
     * Disabling endDate datePicker if a startDate is not selected
     */
    const disableEndDateField = computed(() => {
      return registerInformation.startDate.trim() === "";
    });

    /**
     * Calculates the minimum date allowed for the endDate datePicker
     */
    const minDateEndDateField = computed(() => {
      return registerInformation.startDate.trim() === "" ||
        minDate > new Date(registerInformation.startDate)
        ? minDateString.value
        : registerInformation.startDate;
    });

    /**
     * Calculates the max date allowed for the endDate datePicker
     */
    const maxDateEndDateField = computed(() => {
      if (registerInformation.startDate.trim() === "") {
        return maxDateString.value;
      }
      const tempDate = new Date(startDateAsDate.value);
      tempDate.setDate(
        startDateAsDate.value.getDate() +
          maxDaysDifferenceBetweenStartAndEndDate
      );
      return maxDate > tempDate ? dateToString(tempDate) : maxDateString.value;
    });

    /**
     * Computes error message for the
     */
    const endDateErrorMessage = computed(
      () =>
        `Select a date between ${minDateEndDateField.value} and ${maxDateEndDateField.value}`
    );

    /**
     * Adds start date and start time together
     */
    const startDateAndTime = computed(
      () =>
        new Date(
          registerInformation.startDate +
            "T" +
            registerInformation.startTime +
            ":00"
        )
    );

    /**
     * Adds end date and end time together
     */
    const endDateAndTime = computed(
      () =>
        new Date(
          registerInformation.endDate +
            "T" +
            registerInformation.endTime +
            ":00"
        )
    );

    //Time
    const timeStatus = ref(InputFieldFeedbackStatus.NONE);
    /**
     * Sets timeStatus to SUCCESS if the selected times are valid, and ERROR if not
     */
    const checkTimeValidity = () => {
      timeStatus.value =
        registerInformation.startTime === "" ||
        registerInformation.endTime === "" ||
        startDateAndTime.value >= endDateAndTime.value
          ? InputFieldFeedbackStatus.ERROR
          : InputFieldFeedbackStatus.SUCCESS;
    };

    /**
     * Disables time pickers if the startDate and endDate are not selected
     */
    const disableTimePickers = computed(
      () =>
        registerInformation.startDate === "" ||
        registerInformation.endDate === ""
    );

    /**
     * Checks if both start and end date is selected, and start and end time is selected
     */
    const isDateAndTimeSelected = computed(() => {
      return (
        registerInformation.startDate.trim() !== "" &&
        registerInformation.endDate.trim() !== "" &&
        registerInformation.startTime.trim() !== "" &&
        registerInformation.endTime.trim() !== ""
      );
    });

    const getAvailableRooms = async () => {
      if (isDateAndTimeSelected.value) {
        const startTime =
          registerInformation.startDate + " " + registerInformation.startTime;
        const endTime =
          registerInformation.endDate + " " + registerInformation.endTime;
        let response: GETAvailableSections;
        if (props.reservationId)
          response = await store.dispatch("getAvailableRooms", {
            times: {
              startTime,
              endTime,
            },
            reservationId: props.reservationId,
          });
        else
          response = await store.dispatch("getAvailableRooms", {
            times: {
              startTime,
              endTime,
            },
          });
        if (response !== null) {
          rooms.value = AvailableRoomsToReservationForm(response);
          mapSections();
        }
      }
    };

    /**
     * When start date/time and end date/time has been added or changed
     */
    watch(
      () =>
        registerInformation.startDate +
        registerInformation.endDate +
        registerInformation.startTime +
        registerInformation.endTime,
      async () => {
        await getAvailableRooms();
      }
    );

    //Number of people
    const amountOfPeopleStatus = ref(InputFieldFeedbackStatus.NONE);
    const amountOfPeopleStatusIsNone = computed(
      () => amountOfPeopleStatus.value === InputFieldFeedbackStatus.NONE
    );

    /**
     * Sets amountOfPeopleStatus to SUCCESS if between 1 and 100 people have been added, else it is set to ERROR
     */
    const checkAmountOfPeopleValidity = () => {
      //+ converts string to number or NaN
      const amountOfPeopleValue = +registerInformation.amountOfPeople;
      if (
        isNaN(amountOfPeopleValue) ||
        amountOfPeopleValue - Math.floor(amountOfPeopleValue) !== 0
      ) {
        amountOfPeopleStatus.value = InputFieldFeedbackStatus.ERROR;
      } else {
        amountOfPeopleStatus.value =
          amountOfPeopleValue >= 1 && amountOfPeopleValue <= 100
            ? InputFieldFeedbackStatus.SUCCESS
            : InputFieldFeedbackStatus.ERROR;
      }
    };

    //The checks to be sent to the button actions
    const checks = ref([
      checkRoomValidity,
      checkSectionValidity,
      checkStartDateValidity,
      checkEndDateValidity,
      checkTimeValidity,
      checkAmountOfPeopleValidity,
    ]);

    //The statuses to be sent to the button actions
    const statuses = ref([
      roomStatus,
      sectionStatus,
      startDateStatus,
      endDateStatus,
      timeStatus,
      amountOfPeopleStatus,
    ]);
    return {
      //Also need the basic registerInformation object to be able to call it from @click which is not possible when using ...toRefs()
      registerInformation,
      ...toRefs(registerInformation),
      rooms,
      roomStatus,
      checkRoomValidity,
      availableSections,
      selectAll,
      removeAll,
      checkSectionValidity,
      sectionStatus,
      handleCheckBoxChange,
      minDate,
      maxDate,
      startDateEndDateDifferenceInDays,
      checkDifferenceStartDateEndDate,
      minDateEndDateField,
      maxDateEndDateField,
      minDateString,
      maxDateString,
      endDateStatus,
      disableEndDateField,
      startDateErrorMessage,
      endDateErrorMessage,
      startDateStatus,
      assignEndDate,
      checkStartDateValidity,
      checkEndDateValidity,
      checkTimeValidity,
      isDateAndTimeSelected,
      timeStatus,
      disableTimePickers,
      amountOfPeopleStatus,
      amountOfPeopleStatusIsNone,
      checkAmountOfPeopleValidity,
      checks,
      statuses,
    };
  },
});
